import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MeditationResults, MeditationResultItem } from '../components/meditation';
import { config } from '../config';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const Container = styled.div`
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: white;
`;

const ResultsSection = styled.div`
  margin-top: 40px;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const InfiniteScrollSentinel = styled.div`
  height: 20px;
  margin: 20px 0;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #999;
  font-size: 1rem;
`;

const New: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchResults, setSearchResults] = useState<MeditationResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [maxResults] = useState(100);
  const [offset, setOffset] = useState(0);

  // Parse URL parameters on mount and restore search state
  useEffect(() => {
    const urlLoaded = parseInt(searchParams.get('loaded') || '0', 10);

    //automatically start search or restore search results
    setHasSearched(true);
    if (urlLoaded > 0) {
      // Restore previous search results by loading all batches up to urlLoaded
      restoreSearchResults(urlLoaded);
    } else {
      // Start fresh search
      performInitialSearch();
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Helper function to build API parameters
  const buildApiParams = (currentOffset: number) => {
    const apiParams = new URLSearchParams();
    
    apiParams.append('offset', currentOffset.toString());
    apiParams.append('limit', '20');

    return apiParams;
  };

  // Function to update URL parameters
  const updateURLParams = (loaded?: number) => {
    const newParams = new URLSearchParams();
    
    if (loaded && loaded > 0) {
      newParams.set('loaded', loaded.toString());
    }

    setSearchParams(newParams);
  };

  // Initial search function (resets results)
  const performInitialSearch = useCallback(async () => {
    setLoading(true);
    setSearchResults([]);
    setOffset(0);
    setTotalLoaded(0);
    setHasMore(true);

    try {
      const apiParams = buildApiParams(0);
      const response = await fetch(`${config.apiUrl}/scripts/new?${apiParams.toString()}`);

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      setSearchResults(meditations);
      setOffset(meditations.length);
      setTotalLoaded(meditations.length);
      setHasMore(meditations.length === 20 && meditations.length < maxResults); // Has more if we got full batch and under limit

      // Update URL with search results
      updateURLParams(meditations.length);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults, setSearchParams]);

  // Load more results function for infinite scroll
  const loadMoreResults = useCallback(async () => {
    if (loading || !hasMore || totalLoaded >= maxResults) return;

    setLoading(true);

    try {
      const apiParams = buildApiParams(offset);
      const response = await fetch(`${config.apiUrl}/scripts/new?${apiParams.toString()}`);

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const newMeditations = data.meditations || [];
      const newTotalLoaded = totalLoaded + newMeditations.length;
      
      setSearchResults(prev => [...prev, ...newMeditations]);
      setOffset(prev => prev + newMeditations.length);
      setTotalLoaded(newTotalLoaded);
      setHasMore(newMeditations.length === 20 && newTotalLoaded < maxResults); // Has more if we got full batch and under limit

      // Update URL with new loaded count
      updateURLParams(newTotalLoaded);
    } catch (error) {
      console.error('Load more error:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, totalLoaded, maxResults, offset]);

  // Restore search results when returning from a meditation page
  const restoreSearchResults = useCallback(async (targetLoaded: number) => {
    setLoading(true);
    setSearchResults([]);
    setOffset(0);
    setTotalLoaded(0);

    try {
      // Load all results up to targetLoaded in one request
      const apiParams = buildApiParams(0);
      apiParams.set('limit', Math.min(targetLoaded, maxResults).toString());
      
      const response = await fetch(`${config.apiUrl}/scripts/new?${apiParams.toString()}`);

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      setSearchResults(meditations);
      setOffset(meditations.length);
      setTotalLoaded(meditations.length);
      setHasMore(meditations.length < maxResults);
    } catch (error) {
      console.error('Restore search error:', error);
      setSearchResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults]);

  // Set up infinite scroll
  const { sentinelRef, loadingRef } = useInfiniteScroll({
    loadMore: loadMoreResults,
    hasMore: hasMore && totalLoaded < maxResults,
    loading: loading
  });

  return (
    <Container>
      <Title>Newest</Title>
      
      <ResultsSection>
        {!hasSearched ? (
          <NoResultsMessage>
            Loading...
          </NoResultsMessage>
        ) : (
          <>
            <MeditationResults 
              meditations={searchResults} 
              loading={false} // Don't show loading state in results grid
            />

            {/* Infinite scroll loading indicator */}
            {loading && searchResults.length > 0 && (
              <LoadingIndicator ref={loadingRef}>
                Loading more meditations...
              </LoadingIndicator>
            )}

            {/* Infinite scroll sentinel */}
            {hasMore && totalLoaded < maxResults && !loading && (
              <InfiniteScrollSentinel ref={sentinelRef} />
            )}

            {/* Show message when no results */}
            {!loading && searchResults.length === 0 && hasSearched && (
              <NoResultsMessage>
                No new meditations available at this time.
              </NoResultsMessage>
            )}
          </>
        )}
      </ResultsSection>
    </Container>
  );
};

export default New;